import { Component, inject, signal, PLATFORM_ID } from '@angular/core';
import { Meta, Title } from '@angular/platform-browser';
import { Api } from '../../services/api';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { ArticlesItems, GetRelatedAuthorsParam } from '../../interface/articles';
import { catchError, of, tap, timeout } from 'rxjs';
import { Navbar } from '../navbar/navbar';
import { Footer } from '../footer/footer';
import { DatePipe, DecimalPipe, isPlatformBrowser } from '@angular/common';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { Widgets } from '../widgets/widgets';
import { Canonical } from '../../services/canonical';
import { WidgetFeatured } from '../widget-featured/widget-featured';
import { PageNotFound } from '../page-not-found/page-not-found';
import { TruncatePipe } from '../../pipes/truncate-pipe';

@Component({
  selector: 'app-authors',
  imports: [
    Navbar,
    Footer,
    Widgets,
    RouterLink,
    DatePipe,
    DecimalPipe,
    PageNotFound,
    TruncatePipe,
    WidgetFeatured,
    NgxSkeletonLoaderModule
  ],
  templateUrl: './authors.html',
  styleUrl: './authors.css',
})
export class Authors {
  title = inject(Title);
  meta = inject(Meta);
  activatedRoute = inject(ActivatedRoute);
  router = inject(Router);
  private api = inject(Api);
  private canonical = inject(Canonical);
  private platformId = inject(PLATFORM_ID);

  isLoading = signal(false);
  pageTitle = signal('');
  articles = signal<ArticlesItems[]>([]);

  isUndefined = signal(false);
  results = signal(0);
  limit = signal(3);
  page = signal(1);
  slug = signal('');
  prevBtn = signal(false);
  nextBtn = signal(false);
  prevPage = signal(0);
  nextPage = signal(0);

  private renderFacebookShareButton(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const parse = () => {
      const fb = (window as any).FB;
      if (typeof fb !== 'undefined' && fb.XFBML) {
        fb.XFBML.parse();
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (parse()) return;

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (parse() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 200);
    }, 100);
  }

  private renderTwitterShareButton(): void {
    if (!isPlatformBrowser(this.platformId)) return;

    const parse = () => {
      const twttr = (window as any).twttr;
      if (typeof twttr !== 'undefined' && twttr.widgets) {
        twttr.widgets.load();
        return true;
      }
      return false;
    };

    setTimeout(() => {
      if (parse()) return;

      let attempts = 0;
      const interval = setInterval(() => {
        attempts++;
        if (parse() || attempts >= 20) {
          clearInterval(interval);
        }
      }, 200);
    }, 100);
  }

  prev() : void {
    this.router.navigate(['/authors', this.slug(), this.limit(), this.prevPage()]);
  }

  next() : void {
    this.router.navigate(['/authors', this.slug(), this.limit(), this.nextPage()]);
  }

  private initialize(obj: GetRelatedAuthorsParam) : void {
    this.isLoading.set(true);
    this.isUndefined.set(false);

    this.api.getAuthorsRelatedArticles(obj)
    .pipe(
      timeout(10000),
      catchError(() => of({
        results: 0,
        page: {
          previous: false,
          next: false
        },
        canonical: null,
        siteTitle: null,
        title: null,
        meta: null,
        items: []
      })),
      tap(() => this.isLoading.set(false))
    ).subscribe(obj => {
      if(obj.items.length == 0){
        this.isUndefined.set(true);
      }else{
        this.results.set(obj.results);
        this.canonical.setCanonicalUrl(obj?.canonical!);
        this.title.setTitle(obj?.siteTitle!);
        this.pageTitle.set(obj?.title!);
        this.meta.addTags(JSON.parse(obj?.meta!));
        this.articles.set(obj.items);
        this.prevBtn.set(obj.page.previous);
        this.nextBtn.set(obj.page.next);
        this.renderFacebookShareButton();
        this.renderTwitterShareButton();
      }
    });
  }

  ngOnInit() : void {
    this.activatedRoute.paramMap.subscribe(param => {
      const slug = param.get('slug');
      const limit = param.get('limit');
      const page = param.get('page');

      this.slug.set(slug!);
      this.limit.set(Number(limit));
      this.page.set(Number(page));
      this.prevPage.set(Number(page) - 1);
      this.nextPage.set(Number(page) + 1);

      this.initialize({
        slug: this.slug(),
        limit: this.limit(),
        page: this.page()
      });
    });
  }
}
